/**
 * Helper functions for picking and manipulating shared argument collections.
 */

// ============ Internal Helpers ============

/**
 * Sorts object keys alphabetically.
 * @param {Object} obj - Object to sort
 * @returns {Object} - New object with sorted keys
 */
export function sortKeys(obj) {
  return Object.fromEntries(Object.entries(obj).sort(([a], [b]) => a.localeCompare(b)));
}

/**
 * Sorts meta keys alphabetically and stamps args with inheritedFrom if not already set.
 * Used in forComponent() to track which component introduced each argument.
 * @param {Object} meta - The meta object containing arg configs
 * @param {string} componentPath - The component path to stamp on args without inheritedFrom
 * @returns {Object} - New object with sorted keys and inheritedFrom stamped
 */
export function sortAndStampMeta(meta, componentPath) {
  return Object.fromEntries(
    Object.entries(meta)
      .map(([key, config]) => [
        key,
        config.inheritedFrom ? config : { ...config, inheritedFrom: componentPath },
      ])
      .sort(([a], [b]) => a.localeCompare(b))
  );
}

/**
 * Gets an arg config from a shared args collection.
 * @param {Object} sharedArgs - The shared args collection (e.g., CONTAINER_ARGS)
 * @param {string} name - The name of the arg to get
 * @returns {Object|undefined} - The arg config or undefined if not found
 */
function getArgConfig(sharedArgs, name) {
  return sharedArgs.meta?.[name];
}

/**
 * Applies overrides to an arg config.
 * Handles the 'default' -> 'defaultValue' and 'defaultWhen' -> 'conditionalDefault' mappings.
 * @param {Object} config - The base arg config to modify
 * @param {Object} overrides - Override values to apply
 * @returns {Object} - The modified config
 */
function applyOverrides(config, overrides) {
  const { default: defaultVal, defaultWhen, ...rest } = overrides;

  // Exclude 'name' and 'rename' from rest since they are identifiers, not config properties
  delete rest.name;
  delete rest.rename;

  return {
    ...config,
    ...rest,
    ...(defaultVal !== undefined && { defaultValue: defaultVal }),
    ...(defaultWhen && { conditionalDefault: { arg: defaultWhen[0], values: defaultWhen[1] } }),
  };
}

/**
 * Picks an arg by name (string spec).
 * @param {Object} sharedArgs - The shared args collection
 * @param {string} name - The arg name to pick
 * @returns {Object|null} - Object with name and config, or null if not found
 */
function pickStringArg(sharedArgs, name) {
  const config = getArgConfig(sharedArgs, name);
  return config ? { name, config: { ...config } } : null;
}

/**
 * Picks an arg with overrides (object spec).
 * Supports `rename` to register the arg under a different key in the resulting metadata.
 * @param {Object} sharedArgs - The shared args collection
 * @param {Object} spec - Object with name, optional rename, and optional overrides
 * @returns {Object|null} - Object with name and config, or null if not found
 */
function pickObjectArg(sharedArgs, spec) {
  const result = pickStringArg(sharedArgs, spec.name);
  if (!result) return null;

  if (spec.rename) result.name = spec.rename;

  result.config = applyOverrides(result.config, spec);
  return result;
}

/**
 * Picks a single arg from a shared args collection.
 * @param {Object} sharedArgs - The shared args collection
 * @param {string|Object} spec - Arg name or spec object with overrides
 * @returns {Object|null} - Object with name and config, or null if not found
 */
function pickSingleArg(sharedArgs, spec) {
  switch (typeof spec) {
    case 'string':
      return pickStringArg(sharedArgs, spec);
    case 'object':
      return pickObjectArg(sharedArgs, spec);
    default:
      return null;
  }
}

// ============ Exported Functions ============

/**
 * Helper to pick specific args from a shared args collection and optionally override defaults.
 *
 * @param {Object} sharedArgs - The shared args collection (e.g., CONTAINER_ARGS)
 * @param {Array<string|Object>} argSpecs - Array of arg names or objects with overrides
 * @returns {Object} - Object with meta containing picked args
 *
 * @example
 * // Simple picking
 * pick(CONTAINER_ARGS, ['fitContent', 'column']);
 *
 * @example
 * // Picking with overrides
 * pick(CONTAINER_ARGS, [
 *   'fitContent',
 *   { name: 'column', default: true },
 *   { name: 'gap', default: '16px', description: 'Custom gap description' },
 *   { name: 'hidden', private: true }
 * ]);
 *
 * @example
 * // Picking with rename (registers under a different key in metadata)
 * pick(TOOLTIP_ARGS, [
 *   { name: 'tooltipOffset', rename: 'offset', default: '8px' },
 *   { name: 'tooltipPlacement', rename: 'placement' },
 * ]);
 */
export function pick(sharedArgs, argSpecs) {
  const result = { meta: {} };

  for (const spec of argSpecs) {
    const picked = pickSingleArg(sharedArgs, spec);
    if (picked) {
      result.meta[picked.name] = picked.config;
    }
  }

  return result;
}

/**
 * Picks all args from a shared args collection.
 * Optionally applies global options and per-arg overrides.
 *
 * @param {Object} sharedArgs - The shared args collection (e.g., CONTAINER_ARGS)
 * @param {Object} [options] - Options to apply
 * @param {boolean} [options.private] - Mark all args as private
 * @param {Object} [options.overrides] - Per-arg overrides keyed by arg name
 * @returns {Object} - Object with meta containing all args
 *
 * @example
 * // Pick all container args
 * pickAll(CONTAINER_ARGS);
 *
 * @example
 * // Pick all container args and mark them private
 * pickAll(CONTAINER_ARGS, { private: true });
 *
 * @example
 * // Pick all flex args with specific default overrides
 * pickAll(FLEX_ARGS, {
 *   overrides: {
 *     alignItems: { default: 'center' },
 *     gap: { default: '8px' },
 *     justifyContent: { default: 'center' },
 *   }
 * });
 *
 * @example
 * // Pick all tooltip args, renaming some for the component's public API
 * pickAll(TOOLTIP_ARGS, {
 *   overrides: {
 *     tooltipOffset: { rename: 'offset', default: '8px' },
 *     tooltipPlacement: { rename: 'placement', default: 'bottom-middle' },
 *   }
 * });
 */
export function pickAll(sharedArgs, options = {}) {
  const { private: isPrivate, overrides = {} } = options;
  const meta = sharedArgs.meta || {};

  const specs = Object.keys(meta).map(name => {
    const baseSpec = { name, ...(isPrivate && { private: true }) };
    return overrides[name] ? { ...baseSpec, ...overrides[name] } : baseSpec;
  });

  return pick(sharedArgs, specs);
}

export default { pick, pickAll, sortKeys, sortAndStampMeta };
